/**
 * Parse a timestamp from the API. Handles ISO strings (with timezone) and legacy
 * "YYYY-MM-DD HH:MM:SS" strings from to_char (UTC wall clock, no suffix).
 */
export const parseTimestamp = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  if (dateStr.includes('T') || dateStr.endsWith('Z')) {
    return new Date(dateStr);
  }
  return new Date(dateStr.replace(' ', 'T') + 'Z');
};

/**
 * Extract date (YYYY-MM-DD) and time (HH:MM) parts in a specific timezone.
 */
export const getDateAndTimeInTimezone = (
  dateStr: string | Date,
  timezone: string
): { date: string; time: string } => {
  const date = typeof dateStr === 'string' ? parseTimestamp(dateStr) : dateStr;
  const datePart = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(date);
  const timePart = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  return { date: datePart, time: timePart };
};

/**
 * Parse a database timestamp that's stored without timezone info.
 * The timestamp is assumed to already be in Eastern Time (from Shopify).
 * This function parses it as a local date without UTC conversion.
 */
export const parseShopifyDate = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  
  // Remove 'T', 'Z', and milliseconds to get clean date parts
  const cleanDateStr = dateStr.replace('T', ' ').replace('Z', '').replace(/\.\d+$/, '');
  const parts = cleanDateStr.split(/[- :]/);
  
  // Create date using local constructor (year, month-1, day, hour, min, sec)
  return new Date(
    parseInt(parts[0]),
    parseInt(parts[1]) - 1,
    parseInt(parts[2]),
    parseInt(parts[3] || '0'),
    parseInt(parts[4] || '0'),
    parseInt(parts[5] || '0')
  );
};

/**
 * Format a database timestamp for display (date only)
 */
export const formatDate = (dateStr: string): string => {
  const date = parseShopifyDate(dateStr);
  return date.toLocaleDateString('en-US');
};

/**
 * Format a database timestamp for display (time only)
 */
export const formatTime = (dateStr: string): string => {
  const date = parseShopifyDate(dateStr);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format a database timestamp for display (date and time)
 */
export const formatDateTime = (dateStr: string): string => {
  return `${formatDate(dateStr)} ${formatTime(dateStr)}`;
};

/**
 * Format a date in a specific timezone (org-level timezone support)
 * @param dateStr - ISO date string or Date object
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @param options - Intl.DateTimeFormat options
 */
export const formatInTimezone = (
  dateStr: string | Date,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const date = typeof dateStr === 'string' ? parseTimestamp(dateStr) : dateStr;
  
  try {
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      ...options,
    });
  } catch (e) {
    // Fallback to local time if timezone is invalid
    console.warn(`Invalid timezone: ${timezone}, falling back to local time`);
    return date.toLocaleString('en-US', options);
  }
};

/**
 * Format date only in a specific timezone
 */
export const formatDateInTimezone = (dateStr: string | Date, timezone: string): string => {
  return formatInTimezone(dateStr, timezone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format time only in a specific timezone
 */
export const formatTimeInTimezone = (dateStr: string | Date, timezone: string): string => {
  return formatInTimezone(dateStr, timezone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format date and time in a specific timezone
 */
export const formatDateTimeInTimezone = (dateStr: string | Date, timezone: string): string => {
  return formatInTimezone(dateStr, timezone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format relative time (e.g., "2 hours ago") in a specific timezone
 */
export const formatRelativeTimeInTimezone = (dateStr: string | Date, timezone: string): string => {
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  const now = new Date();
  
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  // For older dates, show the actual date in timezone
  return formatDateInTimezone(date, timezone);
};

/**
 * Get current time in a specific timezone as a formatted string
 */
export const getCurrentTimeInTimezone = (timezone: string): string => {
  return formatTimeInTimezone(new Date(), timezone);
};

/**
 * Get timezone abbreviation (e.g., 'EST', 'PST')
 */
export const getTimezoneAbbreviation = (timezone: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart?.value || timezone;
  } catch (e) {
    return timezone;
  }
};
